import joi from '@hapi/joi';
import { DueDiligenceAccountHolderDetails, DueDiligenceDetails, DueDiligenceGeneralDetails, DueDiligenceSourceOfWealth, FiscalResidenceChanged } from '@/store/dueDiligence/types';
import { errorHelper } from '@/utils/helperFunctions';
import { DropdownOptionSchema } from '../openAccount/schemas';

const AccountHolderDetailsSchema = (
  accountHolder = '',
  hasFiscalResidenceChanged = false,
) =>
  joi
    .object<DueDiligenceAccountHolderDetails>()
    .unknown(true)
    .keys({
      Name: joi
        .object({
          FirstName: joi
            .string()
            .required()
            .max(100)
            .error((err: any) => errorHelper(err, 'First Name', accountHolder)),
          LastName: joi
            .string()
            .required()
            .max(100)
            .error((err: any) => errorHelper(err, 'Last Name', accountHolder)),
        })
        .required(),
      ContactDetails: hasFiscalResidenceChanged
        ? joi.object({
            EmailAddress: joi
              .string()
              .max(80)
                .regex(/^(?!([^\s@;]+@[^\s@;]+\.[^\s@;]+);\1$)[^\s@;]+@[^\s@;]+\.[^\s@;]+(?:;[^\s@;]+@[^\s@;]+\.[^\s@;]+)?$/i)
              .allow(null)
              .optional()
                .error((err: any) => {
                  const value = err?.[0]?.context?.value;
                  if (typeof value === 'string') {
                    const [firstEmail, secondEmail] = value.split(';');
                    if (firstEmail?.trim().toLowerCase() === secondEmail?.trim().toLowerCase()) {
                      return `${accountHolder} Email addresses must be different`;
                    }
                  }
                  if (err?.[0]?.type === 'string.pattern.base') {
                    return `${accountHolder} Email Address must be a valid email or two different emails separated by a semicolon`;
                  }
                  return errorHelper(err, 'Email Address', accountHolder);
                }),
            MobileNo: joi
              .string()
              .required()
              .min(8)
              .max(20)
              .regex(/^\+\d+$/)
              .error((err: any) => {
                if (err?.[0]?.type === 'string.regex.base') {
                  return `${accountHolder} Mobile Number must be in the format +XX-XXXXXXX (e.g., +49-123456789)`;
                }
                return errorHelper(err, 'Mobile Number', accountHolder);
              }),
          })
        : joi.object({
            EmailAddress: joi
              .string()
              .max(80)
              .regex(/^(?!([^\s@;]+@[^\s@;]+\.[^\s@;]+);\1$)[^\s@;]+@[^\s@;]+\.[^\s@;]+(?:;[^\s@;]+@[^\s@;]+\.[^\s@;]+)?$/i)
              .allow(null)
              .optional()
                .error((err: any) => {
                  const value = err?.[0]?.context?.value;
                  if (typeof value === 'string') {
                    const [firstEmail, secondEmail] = value.split(';');
                    if (firstEmail?.trim().toLowerCase() === secondEmail?.trim().toLowerCase()) {
                      return `${accountHolder} Email addresses must be different`;
                    }
                  }
                  if (err?.[0]?.type === 'string.pattern.base') {
                    return `${accountHolder} Email Address must be a valid email or two different emails separated by a semicolon`;
                  }
                  return errorHelper(err, 'Email Address', accountHolder);
                }),
            MobileNo: joi.string().allow(null).optional(),
          }),
      Address: joi.object({
        Street: joi
          .string()
          .required()
          .error((err: any) => errorHelper(err, 'Street', accountHolder)),
        StreetNo: joi
          .string()
          .allow(null)
          .optional()
          .error((err: any) =>
            errorHelper(err, 'Street Number', accountHolder),
          ),
        PostalCode: joi
          .string()
          .required()
          .max(10)
          .error((err: any) => errorHelper(err, 'Postal Code', accountHolder)),
        Place: joi
          .string()
          .allow(null)
          .optional()
          .error((err: any) => errorHelper(err, 'Place', accountHolder)),
        Country: joi
          .number()
          .required()
          .error((err: any) => errorHelper(err, 'Country', accountHolder)),
      }),
      PersonalInfo: joi.object({
        BirthDate: joi
          .date()
          .required()
          .error((err: any) => errorHelper(err, 'Birth Date', accountHolder)),
        PlaceOfBirth: joi
          .string()
          .allow(null)
          .optional()
          .max(45)
          .error((err: any) =>
            errorHelper(err, 'Place of Birth', accountHolder),
          ),
        CountryOfBirth: joi
          .string()
          .required()
          .max(45)
          .error((err: any) =>
            errorHelper(err, 'Country of Birth', accountHolder),
          ),
        Nationality: joi
          .number()
          .required()
          .error((err: any) => errorHelper(err, 'Nationality', accountHolder)),
      }),
      TaxInfo: joi.when('$accountType', {
        is: joi.valid(400160, 400164),
        then: joi.any().strip(),
        otherwise: joi.object({
          CountryOfFiscalResidence: joi
            .number()
            .required()
            .error(
              (err: any) =>
                ({
                  message: `${accountHolder} must have a valid Country Of Fiscal Residence`,
                } as any),
            ),
          TinNo: joi
            .string()
            .allow(null, '')
            .optional()
            .error((err: any) => errorHelper(err, 'TIN Number', accountHolder)),
          ReasonNoTinNumber: joi.when('TinNo', {
            is: joi.valid('', null),
            then: DropdownOptionSchema.required().error(
              () => 'TIN or reason for no TIN field is required',
            ),
            otherwise: DropdownOptionSchema.optional().allow(null),
          }),
          IsUSTaxResident: joi
            .boolean()
            .allow(null)
            .optional()
            .error((err: any) =>
              errorHelper(err, 'US Tax Resident', accountHolder),
            ),
          IsPolExposed: joi
            .boolean()
            .allow(null)
            .optional()
            .error((err: any) =>
              errorHelper(err, 'Is Politically Exposed', accountHolder),
            ),
        }),
      }),
      EmploymentDetails: joi
        .object({
          JobTitle: joi
            .string()
            .max(50)
            .allow(null)
            .optional()
            .error((err: any) => errorHelper(err, 'Job Title', accountHolder)),
          NameOfEmployer: joi
            .string()
            .max(100)
            .allow(null)
            .optional()
            .error((err: any) =>
              errorHelper(err, 'Employer Name', accountHolder),
            ),
          DurationOfEmployment: joi
            .string()
            .max(100)
            .allow(null)
            .optional()
            .error((err: any) => errorHelper(err, 'Duration of Employment', accountHolder)),
          SectorOfEmployment: joi
            .string()
            .max(100)
            .allow(null)
            .optional()
            .error((err: any) => errorHelper(err, 'Sector of Employment', accountHolder)),
          RetiredRemarks: joi
            .string()
            .max(500)
            .allow(null, '')
            .optional()
            .error((err: any) =>
              errorHelper(err, 'Retired Remarks', accountHolder),
            ),
        })
        .unknown(true)
        .allow(null)
        .optional(),
    });

export const GeneralDetailsSchema = (fiscalResidenceChanged: FiscalResidenceChanged) => joi
    .object<DueDiligenceGeneralDetails>()
    .unknown(true)
    .keys({
        PlatformAccount: joi.object({
            AccountNumber: joi.string().required()
            .error((err: any) => errorHelper(err, 'Account Number')),
            ConsultantEmail: joi.string().allow(null).optional()
            .error((err: any) => errorHelper(err, 'Consultant Email')),
            ConsultantName: joi.string().allow(null).optional(),
            PlatformAccountType: joi.number().allow(null).optional()
            .error((err: any) => errorHelper(err, 'Platform Account Type')),
        }).required(),
        PrimaryAccountHolder: AccountHolderDetailsSchema('First Account Holder', fiscalResidenceChanged.PrimaryAccountHolder.changed).required(),
        SecondaryAccountHolder: AccountHolderDetailsSchema('Secondary Account Holder', fiscalResidenceChanged.SecondaryAccountHolder.changed).allow(null),
        PlannedInvestmentDetails: joi.object({
          PlannedInvestmentVolume: joi.any().allow(null).optional(),
          PlannedInvestmentFrequency: joi.number().allow(null).optional(),
          PlannedInvestmentFrequencyOther: joi.when('PlannedInvestmentFrequency', {
            is: 2,
            then: joi
              .string()
              .trim()
              .max(100)
              .required()
              .error((err: any) => errorHelper(err, 'Other investment frequency',)),
            otherwise: joi.string().max(100).allow(null, '').optional(),
          }),
        }).required(),
    });

export const SourceSchema = joi
    .array()
    .required()
    .items(
    joi.object<DueDiligenceSourceOfWealth>({
        Id: joi.number().required()
        .error((err: any) => errorHelper(err, 'Source Type')),
        Remarks: joi.string().max(800).allow(null).allow("").optional()
        .error((err: any) => errorHelper(err, 'Remarks')),
    })
    )
    .min(1)
    .error(() => 'At least one source of wealth/funds is required');

export const MoreInformationSchema = joi
  .any()
  .allow(null)
  .optional();

